/**
 * errorStorage — Centralized error persistence + App Insights upload.
 *
 * Dual-backend storage: Capacitor Preferences (native) → localStorage (web).
 * Errors are stored locally first (offline-safe), then flushed to App Insights
 * when connectivity is available.
 *
 * Best-in-class pattern: every stored error includes breadcrumb trail,
 * device context, and visibility state — the same data Sentry and Datadog
 * capture per-error.
 *
 * @see handleIndexedDBError.ts — Safari IDB crashes, reloads
 * @see breadcrumbs.ts — ring buffer attached to each error
 */

import { Capacitor } from "@capacitor/core"
import { getBreadcrumbsAsString } from "./breadcrumbs"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface StoredError {
  /** ISO timestamp */
  timestamp: string
  /** Error message */
  message: string
  /** Stack trace (truncated to 2000 chars) */
  stack?: string
  /** React componentStack if from ErrorBoundary */
  componentStack?: string | null
  /** Error category for filtering */
  category: ErrorCategory
  /** Severity: info | warning | error | critical */
  severity: "info" | "warning" | "error" | "critical"
  /** Breadcrumb trail at the time of the error (compact string) */
  breadcrumbs?: string
  /** Source: boot | global | boundary | watchdog | manual */
  source: ErrorSource
  /** Current URL/route */
  url?: string
  /** document.visibilityState */
  visibilityState?: string
  /** document.readyState */
  readyState?: string
  /** Platform: web | ios | android */
  platform?: string
  /** Additional structured data */
  data?: Record<string, string | number | boolean>
}

export type ErrorCategory =
  | "indexeddb"
  | "dexie"
  | "auth"
  | "network"
  | "render"
  | "lifecycle"
  | "unknown"

export type ErrorSource =
  | "boot"
  | "global"
  | "boundary"
  | "watchdog"
  | "manual"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = "totodo_error_storage"
const MAX_ENTRIES = 50
const MAX_STACK_LENGTH = 2000

// ---------------------------------------------------------------------------
// Category detection — classifies errors automatically
// ---------------------------------------------------------------------------

const CATEGORY_PATTERNS: ReadonlyArray<[ErrorCategory, RegExp]> = [
  ["indexeddb", /IndexedDB|IDB|indexeddb|in-progress transaction|cursor|object store/i],
  ["dexie", /Dexie|dexie|DexieError|BulkError/i],
  ["auth", /auth|token|unauthorized|forbidden|401|403|login|session/i],
  ["network", /fetch|network|CORS|timeout|ERR_NETWORK|net::|Failed to fetch|load failed/i],
  ["render", /render|React|component|hook|hydrat|DOM|Element type|createRoot/i],
  ["lifecycle", /visibility|resume|pause|backgrounded|foreground|appState/i],
]

export function categorizeError(message: string, stack?: string): ErrorCategory {
  const searchText = `${message}\n${stack ?? ""}`
  for (const [category, pattern] of CATEGORY_PATTERNS) {
    if (pattern.test(searchText)) return category
  }
  return "unknown"
}

// ---------------------------------------------------------------------------
// Storage backend — localStorage (works on all platforms)
// ---------------------------------------------------------------------------

function readErrors(): StoredError[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function writeErrors(errors: StoredError[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(errors))
  } catch {
    // Storage full — drop oldest half
    try {
      const trimmed = errors.slice(-Math.floor(MAX_ENTRIES / 2))
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed))
    } catch {
      // Fully broken — give up silently
    }
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Log an error to persistent storage.
 * Auto-attaches breadcrumbs, device context, and visibility state.
 * Also forwards to App Insights if available.
 */
export function logError(
  error: unknown,
  options?: {
    source?: ErrorSource
    category?: ErrorCategory
    severity?: StoredError["severity"]
    componentStack?: string | null
    data?: Record<string, string | number | boolean>
  },
): void {
  const message = error instanceof Error ? error.message : String(error)
  let stack = error instanceof Error ? error.stack : undefined

  // Truncate large stacks
  if (stack && stack.length > MAX_STACK_LENGTH) {
    stack = stack.slice(0, MAX_STACK_LENGTH) + "\n…[truncated]"
  }

  const entry: StoredError = {
    timestamp: new Date().toISOString(),
    message,
    stack,
    category: options?.category ?? categorizeError(message, stack),
    severity: options?.severity ?? "error",
    source: options?.source ?? "manual",
    breadcrumbs: getBreadcrumbsAsString(),
    url: typeof window !== "undefined" ? window.location.href : undefined,
    visibilityState: typeof document !== "undefined" ? document.visibilityState : undefined,
    readyState: typeof document !== "undefined" ? document.readyState : undefined,
    platform: Capacitor.getPlatform(),
  }

  if (options?.componentStack) {
    entry.componentStack = options.componentStack.slice(0, MAX_STACK_LENGTH)
  }

  if (options?.data) {
    entry.data = options.data
  }

  // Persist locally
  const existing = readErrors()
  existing.push(entry)
  if (existing.length > MAX_ENTRIES) {
    existing.splice(0, existing.length - MAX_ENTRIES)
  }
  writeErrors(existing)

  // Forward to App Insights (lazy import to avoid circular deps)
  forwardToAppInsights(error instanceof Error ? error : new Error(message), entry)
}

/**
 * Get all stored errors for debug UI / export.
 */
export function getStoredErrors(): StoredError[] {
  return readErrors()
}

/**
 * Clear all stored errors.
 */
export function clearStoredErrors(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Best effort
  }
}

/**
 * Get a compact summary of recent errors for diagnostic display.
 */
export function getErrorSummary(): { total: number; byCategory: Record<string, number> } {
  const errors = readErrors()
  const byCategory: Record<string, number> = {}
  for (const e of errors) {
    byCategory[e.category] = (byCategory[e.category] ?? 0) + 1
  }
  return { total: errors.length, byCategory }
}

// ---------------------------------------------------------------------------
// App Insights forwarding — lazy async to avoid load-order issues
// ---------------------------------------------------------------------------

async function forwardToAppInsights(error: Error, entry: StoredError): Promise<void> {
  try {
    const { trackException } = await import("./client")
    const { SeverityLevel } = await import("./types")

    const severityMap = {
      info: SeverityLevel.Information,
      warning: SeverityLevel.Warning,
      error: SeverityLevel.Error,
      critical: SeverityLevel.Critical,
    } as const

    trackException(error, severityMap[entry.severity], {
      category: entry.category,
      source: entry.source,
      visibilityState: entry.visibilityState ?? "",
      readyState: entry.readyState ?? "",
      breadcrumbs: entry.breadcrumbs ?? "",
      ...(entry.componentStack ? { componentStack: entry.componentStack } : {}),
    })
  } catch {
    // App Insights not available — error is still persisted locally
  }
}
